import {
  Box,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  styled,
  Switch,
  Stack,
  Divider,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useUser } from '@/contexts/useUser';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';
import type BackupVaultPreferences from '@/data/vaults/backupVaultPreferences';

import { Accordion, AccordionDetails, AccordionSummary } from '../accordion';
import { PanelWrapper } from '../panels/panelWrapper';
import CopyableCode from '../shared/copyableCode';
import { ReadMoreLink } from '../shared/readMoreLink';

const ColorRadioButton = styled(Radio)<{ value: string }>((props) => ({
  display: 'inline',
  width: '24px',
  height: '24px',
  backgroundColor: props.value,
  // borderRadius: '6px',
  margin: '6px',
  '&:first-child': {
    marginLeft: 0,
  },
  '&:hover': {
    backgroundColor: props.value,
    opacity: 0.7,
  },
  '& svg': { display: 'none' },
  '&.Mui-checked': {
    border: '2px solid gray',
  },
  '&.Mui-disabled': {
    opacity: 0.5,
  },
}));

const SupportedAWSRegions = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'ca-central-1',
  'eu-west-1',
  'eu-west-2',
  'eu-central-1',
];

const SupportedAzureRegions = [
  'eastus',
  'eastus2',
  'centralus',
  'northcentralus',
  'southcentralus',
  'westus',
  'westus2',
  'westus3',
  'westcentralus',
  'canadacentral',
  'canadaeast',
  'westeurope',
  'northeurope',
  'australiacentral',
  'australiaeast',
  'australiasoutheast',
  'francecentral',
  'germanywestcentral',
  'japaneast',
  'japanwest',
  'swedencentral',
  'uksouth',
  'ukwest',
  'centralindia',
  'westindia',
  'southindia',
];

const SupportedGCPRegions = [
  'us-east1',
  'us-central1',
  'us-east4',
  'us-west1',
  'us-west2',
  'us-west3',
  'us-west4',
  'northamerica-northeast1',
  'europe-west1',
  'europe-west2',
  'europe-west3',
  'europe-west4',
  'europe-central2',
  'asia-east1',
  'asia-southeast1',
  'asia-south1',
  'asia-northeast1',
  'australia-southeast1',
  'southamerica-east1',
];

export const SupportedRegions: Partial<Record<Provider, string[]>> = {
  [Provider.Aws]: SupportedAWSRegions,
  [Provider.Azure]: SupportedAzureRegions,
  [Provider.Gcp]: SupportedGCPRegions,
};

interface EditBackupVaultPanelProps {
  entity: BackupVault;
  onChange: () => void;
  onClose: () => void;
}

enum EditMode {
  Create,
  Edit,
}

export const VaultColors = [
  '#FEB2F1',
  '#8AE3FF',
  '#BDBEF7',
  '#E3B2A1',
  '#D1B9C0',
  '#A1FFD8',
];

export function EditBackupVaultPanel(props: EditBackupVaultPanelProps) {
  const [data, setData] = useState(props.entity);
  const [error, setError] = useState<string | undefined>();
  const dal = useDAL();
  const [loading, setLoading] = useState(false);
  const mode = props.entity.id ? EditMode.Edit : EditMode.Create;
  const { body: pref, isLoading: prefLoading } = dal.preferences.getAccountPref(
    PreferencesKey.BackupVault
  );
  const { user } = useUser();
  const { body: vaultAccounts, isLoading: loadingAccounts } =
    dal.vaults.getAccounts();
  const { VaultCmk } = useFeatureFlags();
  const [customKey, setCustomKey] = useState(
    !!data.vaultAttributes.aws?.encryptionKey
  );

  const { body: restoreAccounts } = dal.cloudAccounts.restore.list();

  useEffect(() => {
    if (!data.vaultAccountId && vaultAccounts?.accounts.length) {
      setData({
        ...data,
        vaultAccountId: vaultAccounts.accounts[0].id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultAccounts]);

  function validate(): boolean {
    if (!data.name || data.name.length < 5) {
      setError('Name must be at least 5 characters long');
      return false;
    }

    const regexp = /^[a-zA-Z0-9-_ ]+$/;
    if (data.name.search(regexp) === -1) {
      setError('Name can only contain letters, numbers, and hyphens');
      return false;
    }

    if (mode === EditMode.Create) {
      if (!data.vaultAttributes.cloudProvider) {
        setError('Please select a cloud provider');
        return false;
      }

      if (!data.vaultAccountId) {
        setError('Please select an account');
        return false;
      }

      if (!data.region) {
        setError('Please select a region');
        return false;
      }
    }

    return true;
  }

  function clearValidationError() {
    setError(undefined);
  }

  async function save() {
    if (!validate()) return;

    setLoading(true);
    try {
      if (data.id) {
        await dal.vaults.update(data);
      } else {
        const res = await dal.vaults.create(data);
        data.id = res?.id || '';
      }
    } catch (e) {
      const err = e as { code: number; body: { error: string } };
      setLoading(false);
      const message = err.code === 403 ? err.body.error : '';
      setError(`Failed to create backup vault ${message}`);
      return;
    }

    if (data.backgroundColor && !prefLoading) {
      if (!pref?.value) {
        await dal.preferences.updateAccountPref(PreferencesKey.BackupVault, {
          colors: [{ id: data.id, color: data.backgroundColor }],
        } satisfies BackupVaultPreferences);
      } else {
        const vpref = pref.value?.colors.find((p) => p.id === data.id);
        if (vpref) {
          vpref.color = data.backgroundColor;
        } else {
          pref.value?.colors.push({
            id: data.id,
            color: data.backgroundColor,
          });
        }

        await dal.preferences.updateAccountPref(
          PreferencesKey.BackupVault,
          pref.value
        );
      }
    }

    props.onChange();
  }

  return (
    <>
      <PanelWrapper
        header={{
          onClose: props.onClose,
          title: props.entity.id ? 'Edit vault' : 'Create vault',
        }}
      >
        <Box
          className='flex flex-grow flex-col p-[40px]'
          overflow='scroll'
          gap='24px'
        >
          <Typography variant='body1'>
            Vaults are where your Eon snapshots are stored. Note that once a
            vault is created, only its name and color can be changed.
          </Typography>
          <FormControl size='small' className='w-full'>
            <FormLabel>{'Name'}</FormLabel>
            <TextField
              disabled={loading}
              size='small'
              defaultValue={data.name}
              inputProps={{ 'data-testid': 'vault-name' }}
              onChange={(event) => {
                clearValidationError();
                setData({
                  ...data,
                  name: event.target.value,
                });
              }}
            />
          </FormControl>
          <FormControl size='small' className='w-full'>
            <FormLabel>Cloud</FormLabel>
            <Select
              disabled={loading || mode === EditMode.Edit}
              value={data.vaultAttributes.cloudProvider}
              data-testid='vault-cloud-provider'
              onChange={(event) => {
                clearValidationError();
                setData({
                  ...data,
                  vaultAttributes: {
                    cloudProvider: event.target.value as Provider,
                  },
                  vaultAccountId: '',
                });
              }}
            >
              <MenuItem
                value={Provider.Aws}
                data-testid='vault-cloud-provider-AWS'
                disabled={!user?.eonAccount?.awsSubscriptionId}
              >
                {CloudProviders[Provider.Aws].displayName}
              </MenuItem>
              <MenuItem
                value={Provider.Gcp}
                data-testid='vault-cloud-provider-GCP'
                disabled={!user?.eonAccount?.gcpSubscriptionId}
              >
                {CloudProviders[Provider.Gcp].displayName}
              </MenuItem>
              <MenuItem
                value={Provider.Azure}
                data-testid='vault-cloud-provider-Azure'
                disabled={!user?.eonAccount?.azureSubscriptionId}
              >
                {CloudProviders[Provider.Azure].displayName}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size='small' className='w-full'>
            <FormLabel>{'Account'}</FormLabel>
            <Select
              disabled={loading || loadingAccounts || mode === EditMode.Edit}
              value={data.vaultAccountId}
              data-testid='vault-cloud-account'
              onChange={(event) => {
                clearValidationError();
                setData({ ...data, vaultAccountId: event.target.value });
              }}
            >
              {vaultAccounts?.accounts
                ?.filter(
                  (a) => a.cloudProvider === data.vaultAttributes.cloudProvider
                )
                .map((account, i) => (
                  <MenuItem
                    value={account.id}
                    key={i}
                    data-testid={`vault-cloud-account-${i}`}
                  >
                    {account.name} ({account.providerAccountId})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl size='small' className='w-full'>
            <FormLabel>{'Region'}</FormLabel>
            <Select
              disabled={
                loading ||
                loadingAccounts ||
                mode === EditMode.Edit ||
                data?.vaultAccountId === ''
              }
              data-testid='vault-region'
              value={data.region}
              onChange={(event) => {
                clearValidationError();
                setData({ ...data, region: event.target.value });
              }}
            >
              {SupportedRegions[data.vaultAttributes.cloudProvider]?.map(
                (region, i) => {
                  return (
                    <MenuItem
                      value={region}
                      key={i}
                      data-testid={`vault-region-${region}`}
                    >
                      {region}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </FormControl>
          <FormControl
            size='small'
            className='w-full flex flex-row items-center justify-between'
          >
            <Stack>
              <FormLabel>{'Display color'}</FormLabel>
              <RadioGroup
                className='flex-row'
                aria-labelledby='demo-controlled-radio-buttons-group'
                name='controlled-radio-buttons-group'
                value={data.backgroundColor || VaultColors[0]}
                onChange={(event) => {
                  setData({ ...data, backgroundColor: event.target.value });
                }}
              >
                {VaultColors.map((color) => (
                  <ColorRadioButton
                    data-testid={`vault-color-btn`}
                    key={color}
                    value={color}
                    inputProps={{
                      ['data-testid' as string]: `vault-color-${color}`,
                    }}
                    disabled={loading}
                  />
                ))}
              </RadioGroup>
            </Stack>
          </FormControl>
          {VaultCmk && data.vaultAttributes.cloudProvider === Provider.Aws && (
            <Stack gap='12px'>
              <Divider sx={{ margin: '0 -40px' }} />
              <Accordion>
                <AccordionSummary>
                  <Typography variant='h4'>Advanced settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box className='w-full'>
                    <Typography variant='body1' component={'span'}>
                      By using your own KMS key, you assume responsibility for
                      securing the key from unauthorized access and deletion.
                      Without this key, snapshots in the vault can&apos;t be
                      retrieved.
                      <br />
                      <ReadMoreLink href='/docs/kb/feature-previews/customer-managed-vault-keys' />
                    </Typography>

                    <Box className='flex justify-between items-center mt-[40px] mb-[12px]'>
                      <Typography>{`Use my own KMS key`}</Typography>
                      <Switch
                        size='small'
                        disabled={
                          loading || loadingAccounts || mode === EditMode.Edit
                        }
                        checked={customKey}
                        onChange={() => {
                          setCustomKey(!customKey);
                          if (!customKey) {
                            setData({
                              ...data,
                              vaultAttributes: {
                                ...data.vaultAttributes,
                                aws: {
                                  ...data.vaultAttributes.aws,
                                  encryptionKey: '',
                                },
                              },
                            });
                          }
                        }}
                      />
                    </Box>

                    {customKey && (
                      <Stack gap='24px' className='mt-[40px]'>
                        <FormControl size='small' className='w-full px-[0px]'>
                          <FormLabel>{'KMS key ARN'}</FormLabel>
                          <TextField
                            disabled={
                              loading ||
                              loadingAccounts ||
                              mode === EditMode.Edit
                            }
                            size='small'
                            defaultValue={
                              data.vaultAttributes.aws?.encryptionKey
                            }
                            inputProps={{
                              'data-testid': 'vault-encryption-key',
                            }}
                            onChange={(event) => {
                              clearValidationError();
                              setData({
                                ...data,
                                vaultAttributes: {
                                  ...data.vaultAttributes,
                                  aws: {
                                    ...data.vaultAttributes.aws,
                                    encryptionKey: event.target.value,
                                  },
                                },
                              });
                            }}
                            placeholder='Example: arn:aws:kms:us-east-1:123456789012:key/f6e3953c-1ee8-5da5-b9a4-66ca5095ffda'
                          />
                        </FormControl>
                        <FormControl size='small'>
                          <Typography className='mb-[12px]' variant='body1'>
                            To grant Eon access to the key, paste these{' '}
                            statements in the KMS key policy:
                          </Typography>
                          <CopyableCode
                            code={`{
  "Sid": "Allow key usage in vault account",
  "Effect": "Allow",
  "Principal": {
    "AWS": "arn:aws:iam::${vaultAccounts?.accounts.find((a) => a.id === data.vaultAccountId)?.providerAccountId || '<VaultAccountId>'}:root"
  },
  "Action": [
    "kms:Encrypt",
    "kms:Decrypt",
    "kms:GenerateDataKey"
  ],
  "Resource": "*"
},
{
  "Sid": "Allow key usage for restore node role and db query",
  "Effect": "Allow",
  "Principal": {
    "AWS": [${restoreAccounts?.accounts
      .filter((x) => x.cloudProvider === Provider.Aws)
      .map(
        (a) =>
          `
      "arn:aws:iam::${a.providerAccountId}:role/EonRestoreAccountRole"
      "arn:aws:iam::${a.providerAccountId}:role/EonRestoreNodeRole"`
      )
      .join(',')}
    ]
  },
  "Action": [
    "kms:Encrypt",
    "kms:Decrypt",
    "kms:GenerateDataKey"
  ],
  "Resource": "*"
}`}
                          />
                        </FormControl>
                      </Stack>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Divider sx={{ margin: '0 -40px' }} />
            </Stack>
          )}
          {error && (
            <div style={{ height: '25px', marginTop: '12px' }}>
              <Typography color='error' variant='body2'>
                <i className='clarity-error-line text-[22px] text-error align-middle mb-[2px]' />
                {error}
              </Typography>
            </div>
          )}
        </Box>
        <Box className='flex justify-end px-[20px] py-[16px]'>
          <Button
            disabled={loading}
            size='small'
            className='mr-[12px]'
            onClick={props.onClose}
          >
            {'Cancel'}
          </Button>
          <Button
            disabled={loading}
            size='small'
            variant='contained'
            data-testid='save-vault'
            onClick={() => {
              void save();
            }}
          >
            {props.entity.id ? 'Save Changes' : 'Create'}
          </Button>
        </Box>
      </PanelWrapper>
    </>
  );
}
